import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import InlineError from "../../../shared/ui/Field/InlineError";
import TextInput from "../../../shared/ui/Field/TextInput";
import {
  UK_DATE_PLACEHOLDER,
  UK_DATETIME_PLACEHOLDER,
  parseErrorMessage,
  parseUkrainianEditorText,
  ukrainianFromLocalDateString,
  ukrainianFromLocalDateTimeString,
} from "../lib/ukrainianDateEditor";

function displayFromCanonical(canonical, mode) {
  if (!canonical) return "";
  return mode === "datetime"
    ? ukrainianFromLocalDateTimeString(canonical)
    : ukrainianFromLocalDateString(canonical);
}

function probeShowPicker(nativeType) {
  if (typeof document === "undefined") return false;
  const probe = document.createElement("input");
  probe.type = nativeType;
  return typeof probe.showPicker === "function";
}

const CompanyUkrainianDateField = forwardRef(function CompanyUkrainianDateField(
  {
    id,
    label,
    required = false,
    error,
    mode = "date",
    canonicalValue = "",
    onCanonicalChange,
    onInvalid,
    disabled = false,
    className = "",
    ariaLabel,
  },
  ref,
) {
  const [draft, setDraft] = useState(() =>
    displayFromCanonical(canonicalValue, mode),
  );
  const [localError, setLocalError] = useState(null);
  const [pickerSupported, setPickerSupported] = useState(false);
  const skipSyncRef = useRef(false);
  const nativeRef = useRef(null);
  const shownError = error || localError;
  const errorId = shownError && id ? `${id}-error` : undefined;
  const nativeType = mode === "datetime" ? "datetime-local" : "date";

  useEffect(() => {
    setPickerSupported(probeShowPicker(nativeType));
  }, [nativeType]);

  useEffect(() => {
    if (skipSyncRef.current) {
      skipSyncRef.current = false;
      return;
    }
    setDraft(displayFromCanonical(canonicalValue, mode));
    setLocalError(null);
  }, [canonicalValue, mode]);

  const emitCanonical = (next) => {
    const normalized = next || "";
    if (normalized === (canonicalValue || "")) {
      return;
    }
    skipSyncRef.current = true;
    if (typeof onCanonicalChange === "function") {
      onCanonicalChange(normalized);
    }
  };

  const commitDraft = (nextDraft, { reformat, announceInvalid } = {}) => {
    const parsed = parseUkrainianEditorText(nextDraft, mode);
    if (parsed.status === "empty") {
      emitCanonical("");
      if (reformat) setDraft("");
      setLocalError(null);
      return parsed;
    }
    if (parsed.status === "valid") {
      emitCanonical(parsed.canonical);
      if (reformat) setDraft(parsed.display);
      setLocalError(null);
      return parsed;
    }
    if (announceInvalid) {
      setLocalError(parseErrorMessage(mode));
    }
    return parsed;
  };

  useImperativeHandle(ref, () => ({
    commit() {
      const parsed = commitDraft(draft, { reformat: true, announceInvalid: true });
      if (parsed.status === "empty") {
        if (required) {
          return { ok: false, error: parseErrorMessage(mode) };
        }
        return { ok: true, value: "" };
      }
      if (parsed.status === "valid") {
        return { ok: true, value: parsed.canonical };
      }
      return { ok: false, error: parseErrorMessage(mode) };
    },
    focus() {
      if (!id || typeof document === "undefined") return;
      const node = document.getElementById(id);
      if (node && typeof node.focus === "function") node.focus();
    },
  }));

  const placeholder =
    mode === "datetime" ? UK_DATETIME_PLACEHOLDER : UK_DATE_PLACEHOLDER;
  const pickerLabel =
    mode === "datetime"
      ? "Обрати дату й час у календарі"
      : "Обрати дату в календарі";

  const editor = (
    <div className="feature-company-work__date-editor">
      <TextInput
        id={id}
        type="text"
        value={draft}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck="false"
        disabled={disabled}
        required={required}
        aria-label={ariaLabel}
        aria-invalid={shownError ? "true" : undefined}
        aria-describedby={errorId}
        onChange={(event) => {
          const next = event.target.value;
          setDraft(next);
          commitDraft(next);
        }}
        onBlur={() => {
          commitDraft(draft, { reformat: true, announceInvalid: true });
        }}
        onInvalid={onInvalid}
      />
      {pickerSupported ? (
        <>
          <button
            type="button"
            className="feature-company-work__date-picker-btn"
            aria-label={pickerLabel}
            disabled={disabled}
            onClick={() => {
              const node = nativeRef.current;
              if (!node || typeof node.showPicker !== "function") return;
              try {
                node.showPicker();
              } catch (_err) {
                /* unsupported in this user-agent; text editor stays usable */
              }
            }}
          >
            <i className="fa-solid fa-calendar" aria-hidden="true" />
          </button>
          <input
            ref={nativeRef}
            type={nativeType}
            className="visually-hidden"
            tabIndex={-1}
            aria-hidden="true"
            value={canonicalValue || ""}
            disabled={disabled}
            onChange={(event) => {
              const next = event.target.value || "";
              emitCanonical(next);
              setDraft(displayFromCanonical(next, mode));
              setLocalError(null);
            }}
          />
        </>
      ) : null}
    </div>
  );

  if (!label) {
    return (
      <>
        {editor}
        {shownError ? (
          <InlineError id={errorId} className="ui-field__error">
            {shownError}
          </InlineError>
        ) : null}
      </>
    );
  }

  const classes = [
    "ui-field",
    "ui-form__field",
    shownError ? "ui-field--invalid" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes}>
      <label htmlFor={id} className="ui-field__label">
        {label}
        {required ? (
          <span className="ui-field__required" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      <div className="ui-field__control">{editor}</div>
      {shownError ? (
        <InlineError id={errorId} className="ui-field__error">
          {shownError}
        </InlineError>
      ) : null}
    </div>
  );
});

export default CompanyUkrainianDateField;
